import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { CheckSquare, Play, Check, Pause, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import ViewToggle, { useViewMode } from '@/components/ui/ViewToggle';
import { fetchMyTasks, patchTask, submitTask, handbackTask, SUBMIT_STAGES, submitLabel, stepLabel, type Task } from '@/lib/queries';
import PromptDialog from '@/components/ui/PromptDialog';
import Countdown from '@/components/ui/Countdown';
import { Undo2 } from 'lucide-react';
import { Send } from 'lucide-react';
import { cn } from '@/lib/cn';
import MeetingActions from '@/components/meetings/MeetingActions';
import WaitingOnMe from '@/components/creative/WaitingOnMe';

const STATUS_COLOR: Record<Task['status'], string> = {
  pending:     'bg-slate-700/40 text-slate-300',
  in_progress: 'bg-blue-500/15 text-blue-300',
  submitted:   'bg-purple-500/15 text-purple-300',
  done:        'bg-emerald-500/15 text-emerald-300',
  blocked:     'bg-red-500/15 text-red-300',
  cancelled:   'bg-slate-800/40 text-slate-500',
};

type Filter = 'open' | 'today' | 'overdue' | 'done' | 'all';

export default function MyTasksPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('open');
  const [view, setView] = useViewMode('tasks', 'cards');
  const [q, setQ] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['tasks', 'my'],
    queryFn: () => fetchMyTasks(),
  });

  const patch = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Task['status'] }) => patchTask(id, { status }),
    onSuccess: () => {
      toast.success('Updated');
      qc.invalidateQueries({ queryKey: ['tasks', 'my'] });
    },
  });
  const [handback, setHandback] = useState<Task | null>(null);
  const hb = useMutation({
    mutationFn: ({ id, reason, mentions }: { id: string; reason: string; mentions?: string[] }) => handbackTask(id, reason, mentions),
    onSuccess: () => { toast.success('Handed back. The people who assign have been told.'); setHandback(null); qc.invalidateQueries({ queryKey: ['tasks', 'my'] }); },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not hand it back'),
  });
  const submit = useMutation({
    mutationFn: (id: string) => submitTask(id),
    onSuccess: (r) => {
      const what = r.requested_roles.map((x) => x === 'art_director_raws' ? 'AD raws' : x === 'art_director_dual' ? 'AD final' : 'CC final').join(' + ');
      toast.success(`Submitted. ${what} requested, ${r.notified} reviewer${r.notified === 1 ? '' : 's'} notified.`);
      qc.invalidateQueries({ queryKey: ['tasks', 'my'] });
    },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not submit'),
  });

  const today = new Date().toISOString().slice(0, 10);
  const filtered = useMemo(() => {
    let list = data ?? [];
    switch (filter) {
      case 'open':    list = list.filter((t) => !['done', 'cancelled'].includes(t.status)); break;
      case 'today':   list = list.filter((t) => t.due_date === today && t.status !== 'done'); break;
      case 'overdue': list = list.filter((t) => t.due_date && t.due_date < today && !['done', 'cancelled'].includes(t.status)); break;
      case 'done':    list = list.filter((t) => t.status === 'done'); break;
    }
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((t) =>
        t.creative_title.toLowerCase().includes(needle) ||
        t.creative_code.toLowerCase().includes(needle) ||
        t.stage.toLowerCase().includes(needle) ||
        stepLabel(t.stage, t.phase).toLowerCase().includes(needle) ||
        (t.required_designation ?? '').toLowerCase().includes(needle) ||
        (t.notes ?? '').toLowerCase().includes(needle),
      );
    }
    return list;
  }, [data, filter, today, q]);

  const counts = useMemo(() => {
    const list = data ?? [];
    return {
      open:    list.filter((t) => !['done', 'cancelled'].includes(t.status)).length,
      today:   list.filter((t) => t.due_date === today && t.status !== 'done').length,
      overdue: list.filter((t) => t.due_date && t.due_date < today && !['done', 'cancelled'].includes(t.status)).length,
      done:    list.filter((t) => t.status === 'done').length,
      all:     list.length,
    };
  }, [data, today]);

  return (
    <div>
      <PageHeader
        title="My tasks"
        subtitle="Everything waiting on me: my decisions first, then my tasks."
        actions={<ViewToggle mode={view} onChange={setView} />}
      />

      <WaitingOnMe />
      <MeetingActions />

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by creative, code, stage, designation, or notes..."
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-line bg-bg-card text-sm text-muted-strong placeholder:text-muted/60 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/40"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {(['open', 'today', 'overdue', 'done', 'all'] as Filter[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                'text-xs px-3 py-1.5 rounded-full border transition capitalize',
                filter === f
                  ? 'border-accent-400 bg-accent-600/20 text-accent-400'
                  : 'border-slate-800 text-muted hover:text-muted-strong'
              )}
            >
              {f} <span className="font-mono">{counts[f]}</span>
            </button>
          ))}
        </div>
      </div>

      {isLoading && <div className="text-muted text-sm">Loading...</div>}

      {!isLoading && filtered.length === 0 && (
        <Card>
          <div className="text-center py-8 space-y-2">
            <CheckSquare className="mx-auto text-muted" size={36} />
            <p className="text-muted text-sm">Nothing in this filter.</p>
          </div>
        </Card>
      )}

      {view === 'cards' && (
        <div className="space-y-2">
          {filtered.map((t) => (
            <Card key={t.id}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0 space-y-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={cn('px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide', STATUS_COLOR[t.status])}>
                      {t.status.replace('_', ' ')}
                    </span>
                    <span className="text-xs text-muted-strong capitalize">{stepLabel(t.stage, t.phase)}</span>
                    {t.required_designation && (
                      <span className="text-[10px] text-muted">({t.required_designation.replace(/_/g, ' ')})</span>
                    )}
                    {t.priority !== 'normal' && (
                      <span className={cn(
                        'text-[9px] px-1.5 py-0.5 rounded uppercase',
                        t.priority === 'urgent' ? 'bg-red-500/15 text-red-300' :
                        t.priority === 'high'   ? 'bg-orange-500/15 text-orange-300' :
                                                  'bg-slate-700/40 text-slate-300'
                      )}>{t.priority}</span>
                    )}
                  </div>
                  <Link to={`/creatives/${t.creative_id}`} className="block hover:text-accent-400">
                    <div className="text-sm font-medium text-muted-strong truncate">{t.creative_title}</div>
                    <div className="text-xs text-muted font-mono">{t.creative_code}</div>
                  </Link>
                  {t.due_date && (
                    <div className={cn('text-[11px] flex items-center gap-2', t.due_date < today && t.status !== 'done' ? 'text-red-400' : 'text-muted')}>
                      Due {t.due_date}
                      {!['done', 'cancelled'].includes(t.status) && <Countdown due={t.due_at} />}
                    </div>
                  )}
                  {t.notes && <p className="text-[11px] text-muted whitespace-pre-wrap">{t.notes}</p>}
                </div>

                <div className="flex flex-col gap-1.5 shrink-0">
                  <TaskActions t={t} onPatch={(status) => patch.mutate({ id: t.id, status })} onSubmit={() => submit.mutate(t.id)} onHandback={() => setHandback(t)} pending={patch.isPending || submit.isPending} />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {view === 'table' && filtered.length > 0 && (
        <Card>
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-muted border-b border-line">
                  <th className="font-medium py-2 pr-3 whitespace-nowrap">Status</th>
                  <th className="font-medium py-2 pr-3 whitespace-nowrap">Stage</th>
                  <th className="font-medium py-2 pr-3">Creative</th>
                  <th className="font-medium py-2 pr-3 whitespace-nowrap hidden md:table-cell">Code</th>
                  <th className="font-medium py-2 pr-3 whitespace-nowrap hidden md:table-cell">Due</th>
                  <th className="font-medium py-2 pr-3 whitespace-nowrap hidden lg:table-cell">Priority</th>
                  <th className="font-medium py-2 whitespace-nowrap text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} className="border-b border-line last:border-0 hover:bg-bg-soft transition">
                    <td className="py-2.5 pr-3 whitespace-nowrap">
                      <span className={cn('px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide', STATUS_COLOR[t.status])}>
                        {t.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-xs text-muted-strong capitalize whitespace-nowrap">
                      {stepLabel(t.stage, t.phase)}
                      {t.required_designation && (
                        <span className="text-[10px] text-muted ml-1">({t.required_designation.replace(/_/g, ' ')})</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-3">
                      <button
                        onClick={() => navigate(`/creatives/${t.creative_id}`)}
                        className="text-left text-muted-strong hover:text-accent-400 truncate block max-w-[300px]"
                      >
                        {t.creative_title}
                      </button>
                    </td>
                    <td className="py-2.5 pr-3 text-xs font-mono text-accent-400 whitespace-nowrap hidden md:table-cell">
                      {t.creative_code}
                    </td>
                    <td className={cn(
                      'py-2.5 pr-3 text-xs whitespace-nowrap hidden md:table-cell',
                      t.due_date && t.due_date < today && t.status !== 'done' ? 'text-red-400 font-medium' : 'text-muted',
                    )}>
                      {t.due_date || '-'}
                    </td>
                    <td className="py-2.5 pr-3 whitespace-nowrap hidden lg:table-cell">
                      {t.priority !== 'normal' ? (
                        <span className={cn(
                          'text-[9px] px-1.5 py-0.5 rounded uppercase',
                          t.priority === 'urgent' ? 'bg-red-500/15 text-red-300' :
                          t.priority === 'high'   ? 'bg-orange-500/15 text-orange-300' :
                                                    'bg-slate-700/40 text-slate-300',
                        )}>{t.priority}</span>
                      ) : <span className="text-muted">-</span>}
                    </td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      <div className="inline-flex gap-1.5">
                        <TaskActions t={t} onPatch={(status) => patch.mutate({ id: t.id, status })} pending={patch.isPending} compact />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <PromptDialog
        open={!!handback}
        title="Hand this task back?"
        message={handback ? `${handback.stage} on ${handback.creative_code}. Say why; the art director, content creator and admins are told and can move it to someone else.` : ''}
        placeholder="Why can't you do it? (required)"
        multiline mentions
        confirmLabel="Hand back"
        onConfirm={(v, named) => { if (handback && v.trim().length >= 3) hb.mutate({ id: handback.id, reason: v.trim(), mentions: named }); else toast.error('Please write a short reason'); }}
        onCancel={() => setHandback(null)}
      />
    </div>
  );
}

function TaskActions({ t, onPatch, onSubmit, onHandback, pending, compact }: {
  t: Task; onPatch: (status: Task['status']) => void; onSubmit?: () => void; onHandback?: () => void; pending: boolean; compact?: boolean;
}) {
  const size = compact ? 'sm' : 'sm';
  // Production stages hand over to reviewers: one button does the whole hand-over.
  const reviewable = !!onSubmit && SUBMIT_STAGES.includes(t.stage);
  return (
    <>
      {t.status === 'pending' && (
        <Button size={size} icon={<Play size={12} />} onClick={() => onPatch('in_progress')} loading={pending}>
          Start
        </Button>
      )}
      {(t.status === 'in_progress' || t.status === 'submitted') && reviewable && (
        <Button size={size} icon={<Send size={12} />} onClick={onSubmit} loading={pending} title="Closes the task, moves the creative to review and asks the reviewers">
          {submitLabel(t.stage, t.phase)}
        </Button>
      )}
      {(t.status === 'in_progress' || t.status === 'submitted') && (
        <Button size={size} variant={reviewable ? 'ghost' : 'primary'} icon={<Check size={12} />} onClick={() => onPatch('done')} loading={pending}>
          Done
        </Button>
      )}
      {t.status === 'in_progress' && !compact && (
        <Button size={size} variant="ghost" icon={<Pause size={12} />} onClick={() => onPatch('blocked')} loading={pending}>
          Block
        </Button>
      )}
      {t.status === 'blocked' && (
        <Button size={size} variant="secondary" onClick={() => onPatch('in_progress')} loading={pending}>
          Resume
        </Button>
      )}
      {onHandback && ['pending', 'in_progress', 'submitted'].includes(t.status) && (
        <Button size={size} variant="ghost" icon={<Undo2 size={12} />} onClick={onHandback} loading={pending} title="Cannot do it after all: say why and the people who assign will move it">
          Can't do it
        </Button>
      )}
    </>
  );
}
